import type { MilemarkerTeamRow } from '../../hooks/milemarker/useMilemarkerData'
import { teamIcon } from '../../navigation'
import StringUtil from '../../util/StringUtil'
import AppIcon from '../icons/AppIcon'
import MaskedIcon from '../icons/MaskedIcon'
import NavIcon from '../icons/NavIcon'
import { cx } from '../util/cx'
import styles from './MilemarkerScreen.module.css'

interface TeamStandingRowProps {
  team: MilemarkerTeamRow
  flash: boolean
  rowRef?: (element: HTMLButtonElement | null) => void
  onSelect?: () => void
  onFlashEnd: () => void
}

export default function TeamStandingRow({
  team,
  flash,
  rowRef,
  onSelect,
  onFlashEnd,
}: TeamStandingRowProps) {
  const className = cx(
    styles.row,
    team.isUserTeam && styles.rowYou,
    team.isPaceTeam && styles.rowPace,
    flash && styles.rowFlash,
  )

  const content = (
    <>
      <span className={styles.rank}>{team.rank}</span>
      <span className={styles.teamCell}>
        <span className={styles.name}>{team.name}</span>
        <span className={styles.members}>
          <NavIcon src={teamIcon} height={16} color="currentColor" />
          {team.memberCount}
        </span>
      </span>
      <span className={styles.goals}>
        {team.breakdown.map((goal) => (
          <span
            key={goal.label}
            className={cx(styles.goal, goal.isGoalMet && styles.goalMet)}
            title={`${goal.label}: ${goal.amount} / ${goal.threshold}`}
          >
            {goal.kind === 'restDays' ? (
              <AppIcon icon="mdi:moon-waning-crescent" size={30} />
            ) : (
              <MaskedIcon src={goal.icon} size={30} color="currentColor" />
            )}
            <span className={styles.goalTrack}>
              <span
                className={styles.goalFill}
                style={{
                  width: `${Math.min(100, goal.threshold > 0 ? (goal.amount / goal.threshold) * 100 : 100)}%`,
                }}
              />
            </span>
          </span>
        ))}
      </span>
      <span className={styles.rms} data-testid="milemarker-rms">
        {StringUtil.numberFormat(team.progress, 0)}
      </span>
    </>
  )

  if (!onSelect) {
    return (
      <li className={className} data-testid="milemarker-team" data-team-id={team.id}>
        {content}
      </li>
    )
  }

  return (
    <li>
      <button
        type="button"
        ref={rowRef}
        className={className}
        data-testid="milemarker-team"
        data-team-id={team.id}
        onClick={onSelect}
        onAnimationEnd={onFlashEnd}
      >
        {content}
        <AppIcon icon="mdi:chevron-right" size={16} className={styles.chevron} />
      </button>
    </li>
  )
}
